"use client";

import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useUser } from "@clerk/nextjs";
import { Bookmark } from "lucide-react";

import { trackEvent } from "@/lib/analytics";
import { OPEN_DISCOVERY_EVENT } from "./open-discovery";
import Doodle, { DOODLE_LIGHT } from "@/components/doodles";
import { createGuestCheckout } from "@/app/gaseste-terapeut/actions";
import { buildQuickCheckoutUrl } from "@/lib/booking-url";
import { BookingSignupModal } from "@/components/booking/BookingSignupModal";
import { ContactRequestModal } from "@/components/booking/ContactRequestModal";
import {
  DemoRequestModal,
  getPendingDemoTherapistId,
} from "@/components/booking/DemoRequestModal";

/* Pen accents on the free intro-session button, in the same language as the
   home hero's calls to action: a chat sticker on its corner and three strokes
   pointing at it from the side. Both sit outside the label. */
const PAGE_GROUND = { ...DOODLE_LIGHT, ground: "#FFF9F5" };

function IntroCtaDoodles() {
  return (
    <>
      <Doodle
        name="rays"
        delay={0.3}
        strokeWidth={2}
        className="absolute -left-5 top-1/2 size-5 -translate-y-1/2 -rotate-45 sm:-left-7 sm:size-6"
      />
      <Doodle
        name="chat"
        palette={PAGE_GROUND}
        delay={0.6}
        strokeWidth={1.7}
        className="absolute -right-3 -top-3.5 size-8"
      />
    </>
  );
}

export default function TherapistBookingCtas({
  therapistId,
  therapistFirstName,
  therapistAvatarUrl,
  acceptsDiscoverySessions = false,
  hasAvailability = true,
  availabilitySlots = [],
}: {
  therapistId: string;
  therapistFirstName: string | null;
  therapistAvatarUrl: string | null;
  acceptsDiscoverySessions?: boolean;
  /** Whether the therapist has at least one upcoming availability slot.
   *  Drives the demo CTA: scheduled (slot picker) vs contact request (text). */
  hasAvailability?: boolean;
  /** Upcoming slots passed to the DemoRequestModal slot picker. */
  availabilitySlots?: Array<{ startTime: string; endTime: string }>;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isSignedIn } = useUser();
  const [guestLoading, setGuestLoading] = useState(false);
  const [guestError, setGuestError] = useState<string | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);

  // `availabilitySlots` is a server prop fetched with `cache: "no-store"` on a
  // force-dynamic page — fresh per request, but a tab left open never
  // refetches on its own. Refresh on refocus so a client who left this page
  // open sees the therapist's latest slots, not a stale snapshot.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") router.refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
  }, [router]);

  // Auto-open the discovery modal in two scenarios:
  //   a) Arrival with ?action=demo (used by /sesiune-cunoastere LP cards).
  //   b) Return from Google OAuth — the modal stashed a pending request in
  //      sessionStorage right before the redirect; if the stashed therapistId
  //      matches this page, reopen so user picks up exactly where they left
  //      off (otherwise they're dropped on the profile with no signal).
  useEffect(() => {
    if (!acceptsDiscoverySessions) return;
    const fromQuery = searchParams.get("action") === "demo";
    const fromOauth = getPendingDemoTherapistId() === therapistId;
    if (!fromQuery && !fromOauth) return;
    if (hasAvailability) {
      setDemoOpen(true);
      trackEvent("demo_request_open", {
        source: fromOauth ? "oauth_restore" : "lp_auto_open",
        therapist_id: therapistId,
      });
    } else {
      setContactOpen(true);
    }
  }, [searchParams, acceptsDiscoverySessions, hasAvailability, therapistId]);

  // Buttons elsewhere on the page (hero, "Ce te aduce aici?", phone bar)
  // open the same form: the intro with a time picker when there are hours,
  // the contact request when there are none.
  useEffect(() => {
    if (!acceptsDiscoverySessions) return;
    const onOpen = (e: Event) => {
      const source = (e as CustomEvent<{ source?: string }>).detail?.source ?? "profile";
      if (hasAvailability) {
        setDemoOpen(true);
        trackEvent("demo_request_open", { source, therapist_id: therapistId });
      } else {
        setContactOpen(true);
        trackEvent("contact_request_open", { source, therapist_id: therapistId });
      }
    };
    window.addEventListener(OPEN_DISCOVERY_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_DISCOVERY_EVENT, onOpen);
  }, [acceptsDiscoverySessions, hasAvailability, therapistId]);

  async function onGuestCheckout() {
    setGuestLoading(true);
    setGuestError(null);
    trackEvent("guest_checkout_start", { source: "terapeut_profile", therapist_id: therapistId });
    if (isSignedIn) {
      // Quick checkout bypasses the onboarding wall so signed-in users can pay
      // for the first session without being forced through setup.
      window.location.assign(buildQuickCheckoutUrl({ therapistId }));
      return;
    }
    const r = await createGuestCheckout({ therapistId });
    if ("url" in r) {
      router.push(`/guest-redirect?to=${encodeURIComponent(r.url)}`);
    } else {
      setGuestLoading(false);
      setGuestError("Nu am putut porni rezervarea rapidă. Reîncearcă în câteva secunde.");
    }
  }

  function onDemoRequest() {
    trackEvent("demo_request_open", {
      source: "terapeut_profile",
      therapist_id: therapistId,
    });
    setDemoOpen(true);
  }

  return (
    <div className="flex w-full flex-col gap-2.5">

      {acceptsDiscoverySessions && hasAvailability && (
        <button
          type="button"
          onClick={onDemoRequest}
          className="relative flex w-full flex-col items-center justify-center gap-0.5 rounded-lg bg-[#DC7050] px-5 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-[#c25c3e]"
        >
          <IntroCtaDoodles />
          <span>Programează ședința de cunoaștere →</span>
          <span className="text-[12px] font-normal text-white/85">
            Gratuit · 20 de minute · online
          </span>
        </button>
      )}

      {acceptsDiscoverySessions && !hasAvailability && (
        <button
          type="button"
          onClick={() => {
            trackEvent("contact_request_open", {
              source: "terapeut_profile",
              therapist_id: therapistId,
            });
            setContactOpen(true);
          }}
          className="relative flex w-full flex-col items-center justify-center gap-0.5 rounded-lg bg-[#DC7050] px-5 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-[#c25c3e]"
        >
          <IntroCtaDoodles />
          <span>Cere o ședință de cunoaștere →</span>
          <span className="text-[12px] font-normal text-white/85">
            Gratuit · 20 de minute · online
          </span>
        </button>
      )}

      {/* The free intro leads when the therapist offers it (it is what the
          ads promise); the paid first session is then the secondary action. */}
      {acceptsDiscoverySessions ? (
        <button
          type="button"
          disabled={guestLoading}
          onClick={onGuestCheckout}
          className="flex w-full flex-col items-center justify-center gap-0.5 rounded-lg border border-[#264653]/25 bg-white px-5 py-3 text-[14.5px] font-semibold text-[#264653] transition-colors hover:border-[#264653]/50 disabled:opacity-70"
        >
          <span>{guestLoading ? "Se pregătește..." : "Sau rezervă direct prima ședință →"}</span>
          <span className="text-[12px] font-normal text-[#264653]/60">
            Plătești online, îți facem contul automat
          </span>
        </button>
      ) : (
        <button
          type="button"
          disabled={guestLoading}
          onClick={onGuestCheckout}
          className="flex w-full flex-col items-center justify-center gap-0.5 rounded-lg bg-[#264653] px-5 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-[#1d3742] disabled:opacity-70"
        >
          <span>
            {guestLoading ? "Se pregătește..." : "Rezervă prima ședință →"}
          </span>
          <span className="text-[12px] font-normal text-white/80">
            Plătești online, îți facem contul automat
          </span>
        </button>
      )}

      <ContactRequestModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        therapistId={therapistId}
        therapistFirstName={therapistFirstName}
        source="terapeut_profile_fallback"
      />

      <DemoRequestModal
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        therapistId={therapistId}
        therapistFirstName={therapistFirstName}
        availabilitySlots={availabilitySlots}
        source="terapeut_profile"
        onSwitchToContact={() => {
          setDemoOpen(false);
          setContactOpen(true);
        }}
      />

      {!isSignedIn && (
        <button
          type="button"
          onClick={() => {
            trackEvent("save_therapist_to_account", {
              source: "terapeut_profile",
              therapist_id: therapistId,
            });
            setSaveOpen(true);
          }}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-[#264653]/20 px-5 py-3 text-[14px] font-semibold text-[#264653] transition-colors hover:border-[#264653]/45"
        >
          <Bookmark className="size-4" />
          Salvează în cont, decizi mai târziu
        </button>
      )}

      {guestError && (
        <p className="text-center text-[12.5px] leading-relaxed text-[#C9542F]">
          {guestError}
        </p>
      )}

      {/* Save: generic signup that assigns this therapist to the new account. */}
      <BookingSignupModal
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        postSignupTherapistId={therapistId}
        assignTherapistId={therapistId}
        source="therapist-profile"
      />
    </div>
  );
}
